import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SignatureCanvas } from '../../Signature/SignatureCanvas';
import {
  SignatureTypeCode,
  type DocumentSignerDto,
} from '../../../services/documentSignatureService';
import {
  confirmElectronicSignature,
  establishWorkflowSignerSigningSession,
  stepUpSignerIdentity,
  submitCertificateSignature,
  submitHandwrittenSignature,
} from '../../../services/signerSignatureService';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { extractCpfDigits, formatCpfInput } from '../../../utils/cpfMask';

export interface WorkflowSignSignatureModalProps {
  isOpen: boolean;
  documentId: string;
  signer: DocumentSignerDto;
  userEmail: string;
  inviteToken?: string | null;
  onClose: () => void;
  onCompleted: () => void | Promise<void>;
}

const SIGNATURE_ERROR_FALLBACK = 'Não foi possível concluir a assinatura.';

export const WorkflowSignSignatureModal: React.FC<WorkflowSignSignatureModalProps> = ({
  isOpen,
  documentId,
  signer,
  userEmail,
  inviteToken,
  onClose,
  onCompleted,
}) => {
  const [phase, setPhase] = useState<'identity' | 'capture'>('identity');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [confirmationNonce, setConfirmationNonce] = useState('');
  const [resolvedSignerId, setResolvedSignerId] = useState('');
  const [ackTerms, setAckTerms] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPassword, setCertPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const captureSignatureType = signer.signatureType;

  const skipIdentityStep = useMemo(() => {
    const noInvite = !inviteToken?.trim();
    const linkedToWorkflowStep = Boolean(signer.documentWorkflowStepApproverId?.trim());
    return noInvite && linkedToWorkflowStep;
  }, [inviteToken, signer.documentWorkflowStepApproverId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setError('');
    setAckTerms(false);
    setCertFile(null);
    setCertPassword('');

    let cancelled = false;

    if (skipIdentityStep) {
      setPhase('capture');
      setCpf('');
      setBirthDate('');
      setConfirmationNonce('');
      setResolvedSignerId('');
      setLoading(true);
      void establishWorkflowSignerSigningSession(documentId, signer.id)
        .then((r) => {
          if (cancelled) {
            return;
          }
          setConfirmationNonce(r.confirmationNonce);
          setResolvedSignerId(r.signerId || signer.id);
        })
        .catch((err: unknown) => {
          if (cancelled) {
            return;
          }
          setError(apiErrorMessage(err, SIGNATURE_ERROR_FALLBACK));
        })
        .finally(() => {
          if (cancelled) {
            return;
          }
          setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    setPhase('identity');
    setCpf('');
    setBirthDate('');
    setConfirmationNonce('');
    setResolvedSignerId('');
    setLoading(false);
  }, [isOpen, skipIdentityStep, documentId, signer.id]);

  const handleStepUp = async () => {
    const cpfDigits = extractCpfDigits(cpf);
    if (!cpfDigits) {
      setError('Informe o CPF.');
      return;
    }
    if (!birthDate.trim()) {
      setError('Informe a data de nascimento.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await stepUpSignerIdentity(documentId, {
        inviteToken: inviteToken ?? null,
        signerId: signer.id,
        email: userEmail.trim(),
        cpf: cpfDigits,
        birthDate: birthDate.trim(),
      });
      setConfirmationNonce(result.confirmationNonce);
      setResolvedSignerId(result.signerId || signer.id);
      setPhase('capture');
    } catch (err: unknown) {
      setError(apiErrorMessage(err, SIGNATURE_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  const finishAndClose = useCallback(async () => {
    await onCompleted();
    onClose();
  }, [onCompleted, onClose]);

  const handleHandwrittenConfirm = async (pngBase64: string) => {
    if (!confirmationNonce) {
      setError('Valide seus dados antes de assinar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitHandwrittenSignature(documentId, {
        inviteToken: inviteToken ?? null,
        signerId: resolvedSignerId || signer.id,
        confirmationNonce,
        imageBase64: pngBase64,
        contentType: 'image/png',
      });
      await finishAndClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, SIGNATURE_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  const handleElectronicConfirm = async () => {
    if (!ackTerms) {
      setError('É necessário aceitar os termos para assinar.');
      return;
    }
    if (!confirmationNonce) {
      setError('Valide seus dados antes de assinar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await confirmElectronicSignature(documentId, {
        inviteToken: inviteToken ?? null,
        signerId: resolvedSignerId || signer.id,
        confirmationNonce,
        acknowledgedTerms: true,
      });
      await finishAndClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, SIGNATURE_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateSubmit = async () => {
    if (!certFile) {
      setError('Selecione o arquivo do certificado (.pfx ou .p12).');
      return;
    }
    if (!certPassword) {
      setError('Informe a senha do certificado.');
      return;
    }
    if (!confirmationNonce) {
      setError('Valide seus dados antes de assinar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await submitCertificateSignature(documentId, {
        certificateFile: certFile,
        password: certPassword,
        confirmationNonce,
        inviteToken: inviteToken ?? null,
        signerId: resolvedSignerId || signer.id,
      });
      await finishAndClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, SIGNATURE_ERROR_FALLBACK));
    } finally {
      setLoading(false);
    }
  };

  const captureTitle =
    captureSignatureType === SignatureTypeCode.Handwritten
      ? 'Assinatura manuscrita'
      : captureSignatureType === SignatureTypeCode.Certificate
        ? 'Certificado digital'
        : 'Assinatura eletrônica';

  const headerTitle =
    phase === 'identity'
      ? 'Confirmar identidade'
      : skipIdentityStep && !confirmationNonce && loading
        ? 'Preparando assinatura'
        : captureTitle;

  const showCaptureSignature =
    phase === 'capture' && (!skipIdentityStep || Boolean(confirmationNonce));

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={loading ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                <Dialog.Title className="text-lg font-semibold text-gray-900">{headerTitle}</Dialog.Title>

                {phase === 'identity' ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-gray-600">
                      Para assinar como <span className="font-medium">{signer.name}</span>, confirme o CPF e a data de
                      nascimento cadastrados na sua conta.
                    </p>
                    <div>
                      <label htmlFor="wf-sign-email" className="block text-sm font-medium text-gray-700">
                        E-mail
                      </label>
                      <input
                        id="wf-sign-email"
                        type="email"
                        readOnly
                        value={userEmail}
                        className="mt-1 block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                      />
                    </div>
                    <div>
                      <label htmlFor="wf-sign-cpf" className="block text-sm font-medium text-gray-700">
                        CPF
                      </label>
                      <input
                        id="wf-sign-cpf"
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="000.000.000-00"
                        maxLength={14}
                        value={cpf}
                        onChange={(e) => setCpf(formatCpfInput(e.target.value))}
                        disabled={loading}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="wf-sign-birth" className="block text-sm font-medium text-gray-700">
                        Data de nascimento
                      </label>
                      <input
                        id="wf-sign-birth"
                        type="date"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        disabled={loading}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ) : null}

                {phase === 'capture' && skipIdentityStep && !confirmationNonce ? (
                  <div className="mt-6 py-10 text-center text-sm text-gray-600">
                    {loading ? 'Aguarde…' : null}
                  </div>
                ) : null}

                {showCaptureSignature && captureSignatureType === SignatureTypeCode.Handwritten ? (
                  <div className="mt-4">
                    <SignatureCanvas onConfirm={(png) => void handleHandwrittenConfirm(png)} disabled={loading} />
                  </div>
                ) : null}

                {showCaptureSignature && captureSignatureType === SignatureTypeCode.Electronic ? (
                  <div className="mt-4 space-y-4">
                    <p className="text-sm text-gray-600">
                      Ao confirmar, você declara que leu o documento e aceita assinar eletronicamente, com validade
                      jurídica conforme a legislação aplicável.
                    </p>
                    <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={ackTerms}
                        onChange={(e) => setAckTerms(e.target.checked)}
                        disabled={loading}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Li e aceito os termos da assinatura eletrônica.</span>
                    </label>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => void handleElectronicConfirm()}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? 'Processando…' : 'Confirmar assinatura'}
                    </button>
                  </div>
                ) : null}

                {showCaptureSignature && captureSignatureType === SignatureTypeCode.Certificate ? (
                  <div className="mt-4 space-y-3">
                    <div>
                      <label htmlFor="wf-sign-cert" className="block text-sm font-medium text-gray-700">
                        Arquivo do certificado (.pfx / .p12)
                      </label>
                      <input
                        id="wf-sign-cert"
                        type="file"
                        accept=".pfx,.p12,application/x-pkcs12"
                        disabled={loading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) {
                            setCertFile(f);
                          }
                        }}
                        className="mt-1 block w-full text-sm text-gray-700"
                      />
                    </div>
                    <div>
                      <label htmlFor="wf-sign-cert-pass" className="block text-sm font-medium text-gray-700">
                        Senha do certificado
                      </label>
                      <input
                        id="wf-sign-cert-pass"
                        type="password"
                        autoComplete="off"
                        value={certPassword}
                        onChange={(e) => setCertPassword(e.target.value)}
                        disabled={loading}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => void handleCertificateSubmit()}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? 'Enviando…' : 'Assinar com certificado'}
                    </button>
                  </div>
                ) : null}

                {error ? (
                  <p className="mt-3 text-sm text-red-600" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Cancelar
                  </button>
                  {phase === 'identity' ? (
                    <button
                      type="button"
                      onClick={() => void handleStepUp()}
                      disabled={loading}
                      className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                    >
                      {loading ? 'Validando…' : 'Continuar'}
                    </button>
                  ) : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
